import { Link, useLocation } from 'react-router-dom';
import { Button, Drawer } from '@crisellevaldez/speculo-ui';
import { AskRemiSearchButton } from '../AskRemiSearchButton';
import { IconPill } from '../IconPill';
import { MenuIcon } from '../icons/MenuIcon';

import { formatPhoneNumber } from '../../../utils/formatters';
import { IntegrationsModal } from '../../features/client/IntegrationsModal';
import { TeamRosterModal } from '../../features/header/TeamRosterModal';
import { ScheduleCallModal } from '../../features/header/ScheduleCallModal';
import { ReferralModal } from '../../features/header/ReferralModal';
import { AssistantModal } from '../../features/client/AssistantModal';
import { ProfileModal } from '../../features/header/ProfileModal';
import { WebsitesModal } from '../../features/client/WebsitesModal';
import { EmailCampaignsModal } from '../../features/client/EmailCampaignsModal';
import { GeneralKnowledgeBaseModal } from '../../features/client/GeneralKnowledgeBaseModal';
import { TwilioStatusBanner } from '../../features/onboarding/components/TwilioStatusBanner';
import { MLSStatusBanner } from '../../features/onboarding/components/MLSStatusBanner';
import { WebsiteStatusBanner } from '../../features/onboarding/components/WebsiteStatusBanner';
import { IntegrationsStatusBanner } from '../../features/onboarding/components/IntegrationsStatusBanner';
import { CrashCourseStatusBanner } from '../../features/onboarding/components/CrashCourseStatusBanner';
import { DatabaseUploadStatusBanner } from '../../features/flows/DatabaseUploadStatusBanner';
import { RolloutSyncStatusBanner } from '../../features/onboarding/components/RolloutSyncStatusBanner';
import { IntegrationSetupStatusBanner } from '../../features/flows/IntegrationSetupStatusBanner';
import { HandoffNotification } from '../../features/handoff';
import { AskRemiModal } from '../../dashboard/AskRemiModal';
import { useAskRemiStore } from '../../../stores/useAskRemiStore';
import {
  ChevronRight,
  LogIn,
  Settings,
  LayoutDashboard,
  User,
  Users,
  Bot,
  Phone,
  Globe,
  Mail,
  Calendar,
  BookOpen,
  Heart,
  Moon,
  Sun,
  Repeat,
  Zap,
  // Search, // hidden with Ask Remi
  LucideIcon,
} from 'lucide-react';
import { useAuthStore } from '../../../contexts/auth/useAuthStore';
import { useOrganizationStore, useFlywheelEnabled, useAskRemiEnabled } from '../../../stores/useOrganizationStore';
import { useState, useEffect, useCallback, type MouseEvent } from 'react';
import { OrganizationSwitcher } from '../../features/header/OrganizationSwitcher';
import { useUser } from '../../../hooks/useUser';
import { usesThirdPartyIdx } from '../../features/client/onboardingData/legacyIdx';
import { isOnboardingMarkedComplete } from '../../../constants/supportContact';

// Reusable Drawer Menu Item Component
interface DrawerMenuItemProps {
  icon: LucideIcon;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}

const DrawerMenuItem: React.FC<DrawerMenuItemProps> = ({
  icon: Icon,
  label,
  onClick,
  disabled = false,
}) => {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`group flex items-center justify-between p-2.5 px-3 rounded-lg border border-border bg-white dark:bg-card shadow-sm hover:border-amber-400/50 hover:shadow-md transition-all duration-150 w-full text-left ${
        disabled ? 'opacity-50 cursor-not-allowed' : ''
      }`}
    >
      <div className="flex items-center gap-3">
        <IconPill icon={Icon} />
        <span className="text-sm font-medium text-foreground">{label}</span>
      </div>
      <ChevronRight className="w-4 h-4 text-muted-foreground/50 group-hover:text-amber-500 group-hover:translate-x-0.5 transition-all" />
    </button>
  );
};

// Reusable header nav link
interface HeaderNavLinkProps {
  to?: string;
  active: boolean;
  onClick?: () => void;
  block?: boolean;
  children: React.ReactNode;
}

const HeaderNavLink: React.FC<HeaderNavLinkProps> = ({ to, active, onClick, block, children }) => {
  const onGlow = (e: MouseEvent<HTMLElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty('--mouse-x', `${e.clientX - rect.left}px`);
    e.currentTarget.style.setProperty('--mouse-y', `${e.clientY - rect.top}px`);
  };

  const className = `${block ? 'block px-3 py-2 rounded-lg mx-1' : 'px-3 py-1.5 rounded-lg'} text-[12.5px] transition-colors mouse-glow ${
    !to && !block ? 'flex items-center gap-1 ' : ''
  }${active ? 'header-nav-active' : 'header-nav'}`;

  if (to) {
    return (
      <Link to={to} onMouseMove={onGlow} className={`no-link-style ${className}`} onClick={onClick}>
        {children}
      </Link>
    );
  }

  return (
    <button onMouseMove={onGlow} className={className} onClick={onClick}>
      {children}
    </button>
  );
};

export function Header() {
  const { user: authUser, loading: authLoading, signOut } = useAuthStore();
  const { user: userData, isSuperAdmin, loading: userLoading } = useUser();
  const { organization, fetchOrganization } = useOrganizationStore();
  const flywheelEnabled = useFlywheelEnabled();
  const location = useLocation();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Active nav state helper
  const isNavActive = (paths: string[]) =>
    paths.some((p) => location.pathname === p || location.pathname.startsWith(p + '/'));
  const isDashboardActive = isNavActive(['/dashboard']);
  const isContactsActive = isNavActive(['/contacts', '/contact']);
  const isFlowsActive = isNavActive(['/flows']);

  // Active sub-flow (Engage / Convert / Revive) is tracked via the URL hash
  const activeFlow = (location.hash.replace('#', '') || 'engage') as 'engage' | 'convert' | 'revive';
  const flowSubNav: { type: 'engage' | 'convert' | 'revive'; label: string; icon: LucideIcon }[] = [
    { type: 'engage', label: 'Engage', icon: Users },
    { type: 'convert', label: 'Convert', icon: Repeat },
    { type: 'revive', label: 'Revive', icon: Zap },
  ];

  // Reject strings that are entirely whitespace-separated "null" /
  // "undefined" tokens — backends that stringify a tuple of (null, null)
  // surface "null null" as the displayName, which would otherwise pass
  // any single-word check and reach the UI as-is.
  const cleanName = (v?: string | null) => {
    const t = (v ?? '').trim();
    if (!t) return '';
    const tokens = t.toLowerCase().split(/\s+/);
    if (tokens.every((tok) => tok === 'null' || tok === 'undefined')) return '';
    return t;
  };
  const firstName = cleanName(userData?.firstName);
  const lastName = cleanName(userData?.lastName);

  // User initials for avatar
  const userInitials =
    firstName && lastName
      ? `${firstName[0]}${lastName[0]}`.toUpperCase()
      : authUser?.email?.[0]?.toUpperCase() || 'U';
  const [isIntegrationsModalOpen, setIsIntegrationsModalOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isTeamRosterOpen, setIsTeamRosterOpen] = useState(false);
  const [isAssistantModalOpen, setIsAssistantModalOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isWebsitesModalOpen, setIsWebsitesModalOpen] = useState(false);
  const [isEmailCampaignsModalOpen, setIsEmailCampaignsModalOpen] = useState(false);
  // const [isDashboardDropdownOpen, setIsDashboardDropdownOpen] = useState(false);
  const [isScheduleCallModalOpen, setIsScheduleCallModalOpen] = useState(false);
  const [isKnowledgeBaseModalOpen, setIsKnowledgeBaseModalOpen] = useState(false);
  const [isReferralModalOpen, setIsReferralModalOpen] = useState(false);
  const { enabled: askRemiEnabled } = useAskRemiEnabled();
  const {
    isModalOpen: isAskRemiOpen,
    openModal: openAskRemi,
    closeModal: closeAskRemi,
  } = useAskRemiStore();
  // const hasRemiConversation = useAskRemiStore((s) => s.messages.length > 0); // hidden with Ask Remi
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return document.documentElement.classList.contains('dark');
    }
    return false;
  });

  const toggleDarkMode = () => {
    const next = !isDarkMode;

    // Circular reveal animation from the toggle button
    if (document.startViewTransition) {
      document.startViewTransition(() => {
        setIsDarkMode(next);
        document.documentElement.classList.toggle('dark', next);
        localStorage.setItem('theme', next ? 'dark' : 'light');
      });
    } else {
      setIsDarkMode(next);
      document.documentElement.classList.toggle('dark', next);
      localStorage.setItem('theme', next ? 'dark' : 'light');
    }
  };

  // Initialize dark mode from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('theme');
    if (saved === 'dark') {
      document.documentElement.classList.add('dark');
      setIsDarkMode(true);
    }
  }, []);

  // Detect platform for shortcut label — hidden with Ask Remi
  const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.userAgent);

  // Cmd+K / Ctrl+K keyboard shortcut
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!askRemiEnabled) return;
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (isAskRemiOpen) closeAskRemi();
        else openAskRemi();
      }
      if (e.key === 'Escape' && isAskRemiOpen) {
        closeAskRemi();
      }
    },
    [askRemiEnabled, isAskRemiOpen, openAskRemi, closeAskRemi]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return (
    <div className="fixed top-0 left-0 right-0 z-[50]" data-header-container>
      {/* Status Banners - shown at the very top of the page */}
      <TwilioStatusBanner organization={organization} />
      <MLSStatusBanner organization={organization} />
      <WebsiteStatusBanner organization={organization} />
      <IntegrationsStatusBanner organization={organization} />
      <CrashCourseStatusBanner organization={organization} />
      <DatabaseUploadStatusBanner organization={organization} />
      <IntegrationSetupStatusBanner organization={organization} />
      <RolloutSyncStatusBanner organization={organization} />

      {/* Admin Toolbar - Only show for super_admin */}
      {isSuperAdmin && (
        <div className="header-admin backdrop-blur-sm border-b border-border w-full">
          <div className="py-1.5 px-4 md:px-6 3xl:px-14">
            <div className="flex flex-col xl:flex-row items-start xl:items-center justify-between">
              <div className="text-xs font-medium  hidden xl:block">
                <div className="flex items-center">
                  <LayoutDashboard className="h-3.5 w-3.5 mr-1.5" />
                  Admin Toolbar
                </div>
              </div>
              <div className="flex items-center gap-3 w-full lg:w-auto">
                <Link to="/client-administration">
                  <Button
                    variant="outline"
                    size="sm"
                    className="admin-btn whitespace-nowrap shrink-0"
                  >
                    <Settings className="h-4 w-4" />
                    <span className="hidden xl:inline ml-2">Client Administration</span>
                  </Button>
                </Link>
                <div className="flex-1 lg:flex-initial min-w-0">
                  <OrganizationSwitcher />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Header — frosted glass */}
      <header className="header-surface border-b header-border">
        <div className="py-2.5 px-3 md:py-3 md:px-6 3xl:px-14">
          <div className="h-full flex items-center justify-between gap-3 relative">
            {/* Left side navigation */}
            <div className="flex items-center gap-3 md:gap-6 shrink-0">
              <Link to="/" className="shrink-0">
                <img src="/favicon.ico" alt="Speculo" className="h-6 w-6 lg:hidden" />
                <div className="hidden lg:block">
                  <img
                    src="/speculo.ai-black.png"
                    alt="Speculo"
                    className="h-7 header-logo-light"
                  />
                  <img src="/speculo.ai-white.png" alt="Speculo" className="h-7 header-logo-dark" />
                </div>
              </Link>
              <button
                className="lg:hidden header-mobile-btn p-1 rounded-md transition-colors"
                onClick={() => setIsMobileMenuOpen(true)}
              >
                <MenuIcon className="h-5 w-5" />
              </button>
              <nav className="hidden lg:flex items-center gap-0.5">
                <HeaderNavLink to="/dashboard" active={isDashboardActive}>
                  Dashboard
                </HeaderNavLink>
                <HeaderNavLink to="/contacts" active={isContactsActive}>
                  Contacts
                </HeaderNavLink>
                <HeaderNavLink to="/flows" active={isFlowsActive}>
                  Flows
                </HeaderNavLink>
              </nav>
            </div>

            {/* Center: Ask Remi — viewport-centered via absolute positioning.
                Visible whenever the desktop nav is. */}
            {askRemiEnabled && (
              <div className="hidden lg:block lg:absolute lg:left-1/2 lg:-translate-x-1/2 lg:w-[400px]">
                <AskRemiSearchButton
                  onClick={openAskRemi}
                  variant="header"
                  isMac={isMac}
                />
              </div>
            )}

            {/* Right side: notifications + user */}
            <div className="flex items-center gap-1 md:gap-2 shrink-0">
              {organization && flywheelEnabled && <HandoffNotification />}

              {/* Dark Mode Toggle — pill switch (smaller on mobile) */}
              <button
                onClick={toggleDarkMode}
                className="relative w-12 h-6 lg:w-14 lg:h-7 rounded-full header-toggle transition-colors duration-300 shrink-0"
                aria-label="Toggle dark mode"
              >
                <Moon
                  className="absolute left-1.5 top-1/2 -translate-y-1/2 h-2.5 w-2.5 lg:h-3 lg:w-3 text-blue-400/40"
                  fill="currentColor"
                  strokeWidth={1}
                />
                <Sun className="absolute right-1.5 top-1/2 -translate-y-1/2 h-2.5 w-2.5 lg:h-3 lg:w-3 text-amber-400/40" />
                <div
                  className={`absolute top-px left-px w-5 h-5 lg:w-6 lg:h-6 rounded-full bg-white shadow-md transition-transform duration-300 ease-in-out flex items-center justify-center ${
                    isDarkMode ? 'translate-x-0' : 'translate-x-[24px] lg:translate-x-[28px]'
                  }`}
                >
                  {isDarkMode ? (
                    <Moon
                      className="h-3 w-3 lg:h-3.5 lg:w-3.5 text-blue-600"
                      fill="currentColor"
                      strokeWidth={1}
                    />
                  ) : (
                    <Sun
                      className="h-3 w-3 lg:h-3.5 lg:w-3.5 text-amber-500"
                      fill="currentColor"
                      strokeWidth={1.5}
                    />
                  )}
                </div>
              </button>

              <button
                className="flex items-center gap-2 px-1.5 py-1 lg:px-2 lg:py-1.5 rounded-lg header-hover transition-colors"
                onClick={() => {
                  setIsDrawerOpen(true);
                  fetchOrganization();
                }}
              >
                {authLoading || userLoading ? (
                  <span className="text-xs lg:text-sm header-text-muted">Loading...</span>
                ) : (
                  <div className="w-6 h-6 lg:w-7 lg:h-7 rounded-full bg-amber-400 flex items-center justify-center text-[10px] lg:text-xs font-bold text-black shadow-sm">
                    {userInitials}
                  </div>
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile-only nested sub-nav for Flows page */}
      {isFlowsActive && (
        <div className="md:hidden header-surface backdrop-blur-2xl border-b header-border">
          <div className="px-3 py-1.5">
            <div className="flex items-center gap-1 overflow-x-auto">
              {flowSubNav.map((t) => {
                const Icon = t.icon;
                const isActive = activeFlow === t.type;
                return (
                  <button
                    key={t.type}
                    onClick={() => {
                      window.location.hash = t.type;
                    }}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition-colors ${
                      isActive ? 'header-nav-active' : 'header-nav'
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Mobile Navigation Drawer */}
      <Drawer open={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} side="left">
        <Drawer.Header>
          <img src="/speculo.ai-black.png" alt="Speculo" className="h-6 dark:hidden" />
          <img src="/speculo.ai-white.png" alt="Speculo" className="h-6 hidden dark:block" />
        </Drawer.Header>
        <Drawer.Body>
          {askRemiEnabled && (
            <div className="mx-1 mt-2 mb-3">
              <AskRemiSearchButton
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  openAskRemi();
                }}
                variant="drawer"
              />
            </div>
          )}

          <nav className="space-y-1 py-2">
            <Link
              to="/dashboard"
              className={`block px-3 py-2 text-sm rounded-lg mx-1 transition-colors ${
                isDashboardActive
                  ? 'bg-accent text-foreground font-medium'
                  : 'text-muted-foreground hover:bg-accent hover:text-foreground'
              }`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Dashboard
            </Link>

            <div className="border-t border-border my-2" />

            <Link
              to="/contacts"
              className={`block px-3 py-2 text-sm rounded-lg mx-1 transition-colors ${
                isContactsActive
                  ? 'bg-accent text-foreground font-medium'
                  : 'text-muted-foreground hover:bg-accent hover:text-foreground'
              }`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Contacts
            </Link>
            <Link
              to="/flows"
              className={`block px-3 py-2 text-sm rounded-lg mx-1 transition-colors ${
                isFlowsActive
                  ? 'bg-accent text-foreground font-medium'
                  : 'text-muted-foreground hover:bg-accent hover:text-foreground'
              }`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Flows
            </Link>
          </nav>
        </Drawer.Body>
      </Drawer>

      {/* User Settings Drawer */}
      <Drawer open={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} size="lg" side="right">
        <Drawer.Header>
          <span className="text-sm font-medium">{authUser?.email}</span>
        </Drawer.Header>
        <Drawer.Body className="overflow-y-auto">
          {/* User Profile Card */}
          <div className="p-5 mb-4 rounded-xl bg-gradient-to-br from-amber-50 via-white to-amber-50/40 dark:from-amber-500/10 dark:via-card dark:to-amber-500/5 border border-amber-500/50 dark:border-amber-400/40 shadow-[0_0_12px_rgba(217,155,20,0.15)] dark:shadow-[0_0_12px_rgba(251,191,36,0.1)]">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-amber-400 flex items-center justify-center text-sm font-bold text-black">
                {userInitials}
              </div>
              <div>
                <div className="font-semibold text-foreground">
                  {firstName && lastName
                    ? `${firstName} ${lastName}`
                    : firstName ||
                      lastName ||
                      cleanName(userData?.displayName) ||
                      cleanName(authUser?.displayName) ||
                      authUser?.email?.split('@')[0] ||
                      'User'}
                </div>
                <div className="text-xs text-muted-foreground">{authUser?.email}</div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-[11px] text-muted-foreground uppercase tracking-wider mb-0.5">
                  Organization
                </div>
                <div className="text-sm font-medium text-foreground">{organization?.org_name}</div>
              </div>
              <div>
                <div className="text-[11px] text-muted-foreground uppercase tracking-wider mb-0.5">
                  Timezone
                </div>
                <div className="text-sm font-medium text-foreground">{organization?.timezone}</div>
              </div>
              <div>
                <div className="text-[11px] text-muted-foreground uppercase tracking-wider mb-0.5">
                  Phone
                </div>
                <div className="text-sm font-medium text-foreground">
                  {formatPhoneNumber(organization?.phoneNumbers?.outbound ?? '+18662712041')}
                </div>
              </div>
              <div>
                <div className="text-[11px] text-muted-foreground uppercase tracking-wider mb-0.5">
                  Transfer
                </div>
                <div className="text-sm font-medium text-foreground">+1 (866) 271-2041</div>
              </div>
            </div>
          </div>

          <div className="mt-2 space-y-5">
            <div className="grid grid-cols-1 gap-2">
              <DrawerMenuItem
                icon={User}
                label="Organization Profile"
                onClick={() => setIsProfileModalOpen(true)}
              />

              <DrawerMenuItem
                icon={Users}
                label="Users / Team Roster"
                onClick={() => setIsTeamRosterOpen(true)}
              />

              <DrawerMenuItem
                icon={Bot}
                label="AI Assistant"
                onClick={() => setIsAssistantModalOpen(true)}
              />

              <DrawerMenuItem
                icon={Phone}
                label="Integrations"
                onClick={() => organization && setIsIntegrationsModalOpen(true)}
                disabled={!organization}
              />

              {/* IDX Site settings don't apply to orgs on a third-party IDX provider */}
              {organization && !usesThirdPartyIdx(organization.onboardingData?.idx) && (
                <DrawerMenuItem
                  icon={Globe}
                  label="IDX Site"
                  onClick={() => setIsWebsitesModalOpen(true)}
                />
              )}

              {/* Email Campaigns Button */}
              {organization && (
                <DrawerMenuItem
                  icon={Mail}
                  label="eAlerts"
                  onClick={() => setIsEmailCampaignsModalOpen(true)}
                />
              )}

              {/* Knowledge Base Button */}
              {organization && (
                <DrawerMenuItem
                  icon={BookOpen}
                  label="Knowledge Base"
                  onClick={() => setIsKnowledgeBaseModalOpen(true)}
                />
              )}
            </div>

            {/* Divider */}
            <div className="border-t border-border my-1" />

            {/* Referral Section */}
            <button
              onClick={() => setIsReferralModalOpen(true)}
              className="group w-full flex items-center gap-3 p-3 bg-gradient-to-r from-neutral-900 to-neutral-800 dark:from-neutral-800 dark:to-neutral-700 rounded-xl hover:from-neutral-800 hover:to-neutral-700 dark:hover:from-neutral-700 dark:hover:to-neutral-600 transition-all duration-200"
            >
              <div className="p-2 btn-ai-gradient rounded-lg transition-all duration-300 group-hover:scale-105">
                <Heart className="w-3.5 h-3.5 text-white dark:text-zinc-900" />
              </div>
              <div className="flex-1 text-left">
                <p className="text-sm font-medium text-white">Love Speculo.ai?</p>
                <p className="text-xs text-gray-400 group-hover:text-gray-300 dark:text-gray-400 dark:group-hover:text-gray-300">
                  Become an affiliate partner
                </p>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-amber-400 transition-colors" />
            </button>
          </div>
        </Drawer.Body>
        <Drawer.Footer className="flex flex-col gap-3">
          {/* Schedule Call Section */}
          <button
            onClick={() => setIsScheduleCallModalOpen(true)}
            className="group w-full flex items-center gap-3 p-3 bg-warning/10 rounded-xl hover:bg-warning/20 transition-all duration-200"
          >
            <div className="p-2 btn-ai-gradient rounded-lg transition-all duration-300 group-hover:scale-105">
              <Calendar className="w-3.5 h-3.5 text-white dark:text-zinc-900" />
            </div>
            <div className="flex-1 text-left">
              {/* Matches whose calendar opens: onboarding support until Client Admin marks onboarding complete */}
              {isOnboardingMarkedComplete(organization?.onboardingData?.onboarding_complete) ? (
                <>
                  <p className="text-sm font-medium text-foreground">Have any questions?</p>
                  <p className="text-xs text-muted-foreground">
                    Schedule a call with your account strategist
                  </p>
                </>
              ) : (
                <>
                  <p className="text-sm font-medium text-foreground">
                    Have any onboarding questions?
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Schedule a call with our onboarding support
                  </p>
                </>
              )}
            </div>
            <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-amber-600 transition-colors" />
          </button>

          {/* Sign Out Link */}
          <button
            onClick={() => {
              setIsDrawerOpen(false);
              signOut();
            }}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1.5 w-fit"
          >
            <LogIn className="h-3 w-3 rotate-180" />
            Sign Out
          </button>
        </Drawer.Footer>
      </Drawer>

      {/* Modals */}
      {organization?.org_name && (
        <IntegrationsModal
          isOpen={isIntegrationsModalOpen}
          onClose={() => setIsIntegrationsModalOpen(false)}
          selectedOrg={organization}
          onSuccess={() => {
            // Force a fresh fetch of organization data
            fetchOrganization();
          }}
          hideTwilio={true}
        />
      )}

      <TeamRosterModal isOpen={isTeamRosterOpen} onClose={() => setIsTeamRosterOpen(false)} />

      {organization?.org_name && (
        <AssistantModal
          isOpen={isAssistantModalOpen}
          onClose={() => setIsAssistantModalOpen(false)}
          selectedOrg={organization}
          onSuccess={() => {
            // Force a fresh fetch of organization data
            fetchOrganization();
          }}
        />
      )}

      {/* Profile Modal */}
      {organization?.org_name && (
        <ProfileModal
          isOpen={isProfileModalOpen}
          onClose={() => setIsProfileModalOpen(false)}
          organization={organization}
          onSuccess={() => fetchOrganization()}
        />
      )}

      {/* Website Modal */}
      {organization?.org_name && (
        <WebsitesModal
          isOpen={isWebsitesModalOpen}
          onClose={() => setIsWebsitesModalOpen(false)}
          selectedOrg={organization}
          onSuccess={() => {
            // Force a fresh fetch of organization data
            fetchOrganization();
          }}
          customNoWebsiteMessage="To create website, please reach out to our support team, and they'll be happy to help you get set up!"
          showCreateForm={false} // Don't show create form in the header drawer
        />
      )}

      {/* Email Campaigns Modal */}
      {organization?.org_name && (
        <EmailCampaignsModal
          isOpen={isEmailCampaignsModalOpen}
          onClose={() => setIsEmailCampaignsModalOpen(false)}
          selectedOrg={organization}
          onSuccess={() => {
            // Force a fresh fetch of organization data
            fetchOrganization();
          }}
        />
      )}

      {/* Schedule Call Modal */}
      <ScheduleCallModal
        isOpen={isScheduleCallModalOpen}
        onClose={() => setIsScheduleCallModalOpen(false)}
      />

      {/* Knowledge Base Modal */}
      {organization && (
        <GeneralKnowledgeBaseModal
          isOpen={isKnowledgeBaseModalOpen}
          onClose={() => setIsKnowledgeBaseModalOpen(false)}
          selectedOrg={organization}
        />
      )}

      {/* Referral Modal */}
      <ReferralModal isOpen={isReferralModalOpen} onClose={() => setIsReferralModalOpen(false)} />

      {/* Ask Remi Modal */}
      <AskRemiModal />
    </div>
  );
}
